import React from "react";
import Tabs from "common/Tabs/Tabs";
import styled, { css } from "styled-components";
import { segmentedTabs } from "features/bots/BotForm/NewLook/styles";
import {
  AnalyticsPageName,
  BacktestingRunType,
  LanguageNameSpaces,
} from "@shared/types";
import Text from "common/Typography/Text";
import Progress from "common/Progress/Progress";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import { ArrowRightOutlined } from "common/Icons/Icons";
import Flex from "common/Boxes/Flex";
import { TargetsTabs } from "@shared/features/bots/BotForm/common/types";
import {
  TARGETS_STATISTIC_TABS_TO_DISPLAY,
  TARGETS_STATISTIC_TABS_TO_STROKE_COLOR,
} from "@shared/features/bots/BotForm/common/constants";
import useTargetsStatistic from "@shared/features/bots/BotForm/Backtesting/useTargetsStatistic";
import { BacktestingStatisticCard } from "../../common/styles";
import EmptyView from "common/Empty/EmptyView";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TargetsStatisticProps {
  data?: BacktestingRunType;
}

const TargetsStatistic = ({ data }: TargetsStatisticProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { tab, setTab, expanded, getTabContent, onExpandCollapseClick } =
    useTargetsStatistic({ data });
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const getTargetType = (tab: TargetsTabs) => {
    return tab === TargetsTabs.ENTRIES
      ? t("backtesting.statistics.entry")
      : t("backtesting.statistics.take_profit");
  };

  const getTargetProgress = (
    tab: TargetsTabs,
    target: string,
    numOfTrades: number
  ) => {
    return (
      <div key={`${tab}_${target}`}>
        <Text type="secondary">
          {getTargetType(tab)} {target}
        </Text>
        <Progress
          percent={
            data?.number_of_trades
              ? Math.floor((numOfTrades / data?.number_of_trades) * 100)
              : 0
          }
          strokeColor={TARGETS_STATISTIC_TABS_TO_STROKE_COLOR[tab]}
          status="normal"
          style={{ paddingRight: 10 }}
        />
      </div>
    );
  };

  const getTargets = (tab: TargetsTabs, list: [string, number][]) => {
    return list.map(([target, numOfTrades]) => {
      return getTargetProgress(tab, target, numOfTrades);
    });
  };

  const getTargetsItems = (tab: TargetsTabs) => {
    const { mainContent, collapsedContent } = getTabContent(tab);
    return mainContent.length ? (
      <Wrapper onClick={onExpandCollapseClick} $collapsed={!!collapsedContent}>
        {getTargets(tab, mainContent)}
        {collapsedContent && (
          <>
            <ExpandCollapse expanded={expanded[tab]}>
              {getTargets(tab, collapsedContent)}
            </ExpandCollapse>
            <Flex $justifyContent="center">
              <Arrow $expanded={expanded[tab]} />
            </Flex>
          </>
        )}
      </Wrapper>
    ) : (
      <EmptyView
        iconKey={EmptyStatesType.Graph}
        title={t("empty_states.no_data")}
        iconWidth={80}
      />
    );
  };

  const getHelperProps = (tab: TargetsTabs) => ({
    description: t("backtesting.helper_tooltips.targets", {
      targetType: getTargetType(tab).toLowerCase(),
    }),
    analyticsFieldName:
      tab === TargetsTabs.ENTRIES ? "Entries" : "Take Profits",
    analyticsPageName: AnalyticsPageName.BACKTESTING,
    articleId: "9575281",
    title:
      tab === TargetsTabs.ENTRIES
        ? t("backtesting.statistics.entries")
        : t("backtesting.statistics.take_profits"),
  });

  const tabsItems = Object.values(TargetsTabs)?.map((tab) => ({
    key: tab,
    label: (
      <>
        {t(TARGETS_STATISTIC_TABS_TO_DISPLAY[tab])}
        <HelperTooltip {...getHelperProps(tab)} />
      </>
    ),
    children: <div style={{ rowGap: 24 }}>{getTargetsItems(tab)}</div>,
  }));

  return (
    <BacktestingStatisticCard
      style={{ gridArea: "Targets", paddingTop: 8 }}
      $isMobile={isMobile}
    >
      <StyledTabs
        activeKey={tab}
        onTabClick={(tab) => setTab(tab as TargetsTabs)}
        items={tabsItems}
      />
    </BacktestingStatisticCard>
  );
};

export default TargetsStatistic;

const StyledTabs = styled(Tabs)`
  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-list {
    width: 100%;
  }
  &.ant-tabs .ant-tabs-tab {
    flex: 1;
    justify-content: center;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${segmentedTabs};
      &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-list {
        width: 100%;
      }
      .ant-tabs-tab-btn {
        display: inline-flex;
        align-items: center;
      }
      .ant-progress-text {
        color: ${p.theme.colors.primaryText};
        font-size: 12.5px;
        font-variant-numeric: tabular-nums;
      }
      .ant-typography {
        font-size: 12.5px;
      }
    `};
`;
export const Arrow = styled(ArrowRightOutlined)<{ $expanded?: boolean }>`
  fill: ${(p) => p.theme.colors.primaryText};
  transform: rotate(${(p) => (p.$expanded ? 270 : 90)}deg);
  transition: transform 0.35s;
  width: 24px;
  height: 24px;
`;
export const Wrapper = styled.div<{ $collapsed?: boolean }>`
  ${(p) =>
    p.$collapsed &&
    `
    cursor: pointer;
  `};
`;
